import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Database, Globe } from 'lucide-react';
import { COUNTRIES, KIND_LABEL, countryOf, defaultsFor, loadCatalog } from '../../lib/datasets';

/**
 * Country + dataset chips above the chat input, as in the desktop app
 * (analystbro-desktop/src/DataPicker.tsx). Both popovers open upward.
 */
export default function DataPicker({ value, onChange, disabled }) {
  const [open, setOpen] = useState(null); // null | 'country' | 'datasets'
  const [, setVersion] = useState(0);
  useEffect(() => { loadCatalog().then(() => setVersion((v) => v + 1)); }, []);
  const ref = useRef(null);
  const country = countryOf(value.country);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const pickCountry = (code) => {
    // Changing country resets the ticks to that country's defaults.
    onChange(code ? { country: code, datasets: defaultsFor(code) } : { country: null, datasets: [] });
    setOpen(code ? 'datasets' : null);
  };
  const toggle = (slug) =>
    onChange({
      ...value,
      datasets: value.datasets.includes(slug) ? value.datasets.filter((s) => s !== slug) : [...value.datasets, slug],
    });
  const kinds = ['indexed', 'file_index', 'live_source'].filter((k) => country?.datasets.some((d) => d.kind === k));

  const chip = (on) =>
    `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors disabled:opacity-40
     ${on ? 'border-[#2f4a70] text-gray-100 bg-[#1d2634]' : 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500'}`;
  const pop = 'absolute bottom-full left-0 mb-1 w-80 max-h-96 overflow-y-auto rounded-lg border border-gray-700 bg-[#181920] shadow-xl z-20 py-1';
  const title = 'px-3 pt-1 pb-1.5 text-[11px] font-medium text-gray-500';

  return (
    <div className="relative flex items-center gap-1.5" ref={ref}>
      <button type="button" disabled={disabled} className={chip(!!country)}
        onClick={() => setOpen(open === 'country' ? null : 'country')} title="Country the agent should focus on">
        {country ? <span>{country.flag} {country.name}</span> : <><Globe size={13} /> Any country</>}
        <ChevronDown size={12} className="opacity-60" />
      </button>
      <button type="button" disabled={disabled || !country} className={chip(value.datasets.length > 0)}
        onClick={() => setOpen(open === 'datasets' ? null : 'datasets')}
        title={country ? 'Datasets the agent may use' : 'Pick a country first'}>
        <Database size={13} />
        {value.datasets.length ? `${value.datasets.length} dataset${value.datasets.length > 1 ? 's' : ''}` : 'Datasets'}
        <ChevronDown size={12} className="opacity-60" />
      </button>

      {open === 'country' && (
        <div className={pop}>
          <div className={title}>Country</div>
          <button type="button" onClick={() => pickCountry(null)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/5 ${!country ? 'bg-white/5' : ''}`}>
            <Globe size={14} className="text-gray-400" />
            <span className="text-[13px] text-gray-100 flex-1">Any country</span>
            <span className="text-[11px] text-gray-500">no dataset filter</span>
          </button>
          {COUNTRIES.map((c) => (
            <button key={c.code} type="button" onClick={() => pickCountry(c.code)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/5 ${c.code === value.country ? 'bg-white/5' : ''}`}>
              <span>{c.flag}</span>
              <span className="text-[13px] text-gray-100 flex-1">{c.name}</span>
              <span className="text-[11px] text-gray-500">{c.datasets.length} dataset{c.datasets.length === 1 ? '' : 's'}</span>
            </button>
          ))}
        </div>
      )}

      {open === 'datasets' && country && (
        <div className={pop}>
          <div className={title}>{country.flag} {country.name} datasets</div>
          {kinds.map((k) => (
            <div key={k} className="py-1">
              <div className="px-3 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">{KIND_LABEL[k]}</div>
              {country.datasets.filter((d) => d.kind === k).map((d) => (
                <label key={d.slug} className="flex items-start gap-2 px-3 py-1.5 cursor-pointer hover:bg-white/5">
                  <input type="checkbox" className="mt-0.5 accent-blue-500" checked={value.datasets.includes(d.slug)} onChange={() => toggle(d.slug)} />
                  <span className="min-w-0">
                    <span className="block text-[13px] text-gray-100">{d.name}</span>
                    <span className="block text-[11px] text-gray-500">
                      {d.description}
                      {d.itemCount ? ` · ${d.itemCount.toLocaleString()} ${d.kind === 'indexed' ? 'docs' : 'files'}` : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
